/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2019, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
/**
 * Helper Class to post process queries.
 */
define([
	'./PostProcessMeasuresAsSeries',
	'./PostProcessLocalSort',
	'./PostProcessCustomSort',
	'./PostProcessMultiEdgeMeasures',
	'./PostProcessOlapProperties'
], function(PostProcessMeasuresAsSeries, PostProcessLocalSort, PostProcessCustomSort, PostProcessMultiEdgeMeasures, PostProcessOlapProperties) {

	class QueryPostProcessHelper {

		/**
		 * Query response data postprocessing for measures as series.
		 * @param {Object} resultData Query result data
		 * @param {Object} visualization visualization API
		 **/
		static postProcessMeasuresAsSeries(resultData, visualization, queryDefinition) {
			if (resultData) {
				const processor = new PostProcessMeasuresAsSeries({
					queryResultData: resultData,
					visualization: visualization,
					queryDefinition: queryDefinition
				});
				processor.processData();
				processor.destroy();
			}
		}

		/**
		 * Query response data postprocessing for client side aggregated sorting.
		 * @param {Object} resultData Query result data
		 * @param {Object} visualization visualization API
		 **/
		static postProcessAggregatedSort(resultData, visualization) {
			if (resultData) {
				const processor = new PostProcessLocalSort({
					queryResultData: resultData,
					visualization: visualization
				});
				processor.processData();
				processor.destroy();
			}
		}

		/**
		* Query response data postprocessing for client side custom sort.
		* @param {Object} resultData Query result data
		* @param {Object} visualization visualization API
		**/
		static postProcessCustomSort(resultData, visualization) {
			if (resultData) {
				var processor = new PostProcessCustomSort({
					queryResultData: resultData,
					visualization: visualization
				});
				processor.processData();
				processor.destroy();
			}
		}

		/**
		* Query response data postprocessing for multi-edge, multi-measure data.
		* @param {Object} resultData Query result data
		* @param {Object} queryDefinition query definition
		* @param {boolean} [suppressionEnabled=true] - true if suppression is enabled.
		**/
		static postProcessMultiEdgeMeasures(resultData, queryDefinition, suppressionEnabled=true) {
			if (queryDefinition && resultData && resultData.edges && resultData.edges.length > 1) {
				const processor = new PostProcessMultiEdgeMeasures({
					queryResultData: resultData,
					queryDefinition: queryDefinition,
					suppressionEnabled: suppressionEnabled
				});
				processor.processData();
				processor.destroy();
			}
		}

		/**
		* Query response data postprocessing for Olap properties.
		* @param {Object} queryResultData Query result data
		* @param {Object} visualization visualization API
		**/
		static postProcessOlapProperties(queryResultData, visualization) {
			if (queryResultData) {
				const processor = new PostProcessOlapProperties({ queryResultData, visualization });
				processor.processData();
				processor.destroy();
			}
		}
	}

	return QueryPostProcessHelper;
});
